import axios, { AxiosHeaders, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import {
  getMyInfo,
  getPushToken,
  getStoreAdmin,
  getXUserAgent,
  clearByLogout,
  getLocalStorageAfterLogout,
  setLocalStorageAfterLogout
} from 'utils/storages';
import { auth } from 'utils/auth';
import errorHandler from 'apis/errorHandler';
import { getAuthHost, getStoreHost } from './host';

const { CancelToken } = axios;
const source = CancelToken.source();

const baseReqFulfilledInterceptor = (config: InternalAxiosRequestConfig) => {
  const pushToken = getPushToken();
  const reConfig: InternalAxiosRequestConfig = {
    ...config,
    cancelToken: source.token,
    headers: AxiosHeaders.from(config.headers)
  };

  if (pushToken) {
    reConfig.headers.set('X-Push-Token', getXUserAgent());
  }

  return reConfig;
};

export const authClient = axios.create({
  baseURL: getAuthHost(),
  headers: { 'X-User-Agent': getXUserAgent(), 'Content-Type': 'application/json' }
});

export const storeClient = axios.create({
  baseURL: getStoreHost(),
  headers: { 'X-User-Agent': getXUserAgent() }
});

export const authClientWithoutRequestInterceptor = axios.create({
  baseURL: getAuthHost(),
  headers: { 'X-User-Agent': getXUserAgent() }
});

// Info: authClient response, request 설정
authClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    errorHandler && errorHandler(error);
    return Promise.reject(error);
  }
);

authClient.interceptors.request.use(baseReqFulfilledInterceptor, (error) => Promise.reject(error));

// Info: storeClient response, request 설정
// 비즈니스 인증 에러 코드(-2302/-2303/-2304: 인증정보 없음/만료/토큰없음)는 세션 만료로 간주 → 로그아웃 처리
storeClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const status = error?.response?.data?.status;
    if (status === -2302 || status === -2303 || status === -2304) {
      const popupDontView = getLocalStorageAfterLogout();
      await auth.logout().catch(() => {});
      clearByLogout(); // store-web 부가 키 정리
      setLocalStorageAfterLogout(popupDontView);
      if (typeof window !== 'undefined') window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

storeClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const reConfig: InternalAxiosRequestConfig = {
      ...config,
      cancelToken: source.token,
      headers: AxiosHeaders.from(config.headers)
    };

    const meInfo = getMyInfo();
    const storeAdmin = getStoreAdmin();

    if (meInfo) {
      reConfig.headers.set('X-Store-gsid', meInfo.sid);
    } else if (storeAdmin) {
      reConfig.headers.set('X-Store-gsid', storeAdmin.said);
    }

    return reConfig;
  },
  (error) => Promise.reject(error)
);

auth.installInterceptor(storeClient);

authClientWithoutRequestInterceptor.interceptors.request.use(baseReqFulfilledInterceptor, (error) => Promise.reject(error));

export const mockFetcher = {
  get(url: string, delay?: any) {
    const reUrl = `mock/${url}.json`;
    return new Promise<{ data: unknown }>((resolve, reject) => {
      fetch(reUrl, { method: 'GET' })
        .then((res) => res.json())
        .then((data) => {
          setTimeout(() => {
            resolve({ data });
          }, delay || 0);
        })
        .catch((e) => reject(e));
    });
  }
};

const apis = {
  authClient,
  storeClient
};

export default apis;
